<template>
  <!--
    Rückmeldung Kanzlei Polz, 01.10.2026: „…wenn bei diesen über KARS
    ausgeführten Telefonaten sich automatisch danach die Abfrage öffnet, ob man
    die Gesprächsnotiz an irgendjemand oder auch sich selbst weiterleiten
    möchte.“ Global eingehängt (App.vue) – der Anruf kann von jeder Seite
    aus gestartet worden sein.
  -->
  <PhoneNoteSendDialog
    :model-value="!!call"
    :call="call"
    :sending="saving"
    :external-suggestions="participants"
    :title="$gt('Gespräch beendet – Gesprächsnotiz weiterleiten?')"
    with-note
    @update:model-value="onToggle"
    @send="save"
  />
</template>

<script>
import PhoneNoteSendDialog from "@/components/communication/PhoneNoteSendDialog.vue";
import { useCallStore } from "@/stores/callStore";
import { useSnackbarStore } from "@/stores/snackbarStore";
import { getRequestGeneric } from "@/services/backendApiGeneric";
import { notifyPhoneCall, savePortalCallNote } from "@/services/phoneCallsApi";
import { notifyResultMessage } from "@/utils/phoneCallNote";
import { normalizeContactBehavior, normalizeContactBehaviorNote } from "@/utils/contactBehavior";

/** Hat sich das Gesprächsverhalten gegenüber dem gespeicherten Stand geändert? */
function behaviorChanged(call, behavior) {
  if (!behavior) return false;
  const before = normalizeContactBehavior(call.contactBehavior).join(",");
  return before !== behavior.contactBehavior.join(",") ||
    normalizeContactBehaviorNote(call.contactBehaviorNote) !== behavior.contactBehaviorNote;
}

export default {
  name: "CallNotePrompt",
  components: { PhoneNoteSendDialog },
  data() {
    return { saving: false, participants: [] };
  },
  computed: {
    callStore() {
      return useCallStore();
    },
    call() {
      return this.callStore.notePromptCall;
    },
  },
  watch: {
    call(next) {
      this.participants = [];
      if (next?.idCase) this.loadParticipants(next.idCase);
    },
  },
  methods: {
    onToggle(open) {
      if (!open) this.callStore.dismissNotePrompt();
    },
    /** Beteiligte der Akte mit E-Mail als Vorschläge für externe Empfänger. */
    async loadParticipants(idCase) {
      try {
        const involved = await getRequestGeneric(`/protected/cases/${idCase}/involved`);
        this.participants = (Array.isArray(involved) ? involved : [])
          .filter((p) => !p.removedAt && p.email && /@/.test(p.email))
          .map((p) => ({ name: p.name, email: p.email.trim(), role: (p.roles || [])[0] || "" }));
      } catch {
        // Vorschläge sind optional – freie Eingabe bleibt möglich.
      }
    },
    async save(notify, note, behavior) {
      const call = this.call;
      if (!call) return;
      this.saving = true;
      try {
        if ((note || "") !== (call.summary || "") || behaviorChanged(call, behavior)) {
          // Der PUT ersetzt Notiz und Gesprächsverhalten gemeinsam.
          await savePortalCallNote(call.id, note || "", behavior || {
            contactBehavior: normalizeContactBehavior(call.contactBehavior),
            contactBehaviorNote: normalizeContactBehaviorNote(call.contactBehaviorNote),
          });
        }
        if (notify) {
          const result = await notifyPhoneCall(call, notify);
          useSnackbarStore().showSnackbar(notifyResultMessage(result));
        } else {
          useSnackbarStore().showSnackbar({ message: "Gesprächsnotiz gespeichert.", type: "success" });
        }
        this.callStore.dismissNotePrompt(true);
      } catch (e) {
        useSnackbarStore().showSnackbar({ message: e?.message || "Speichern fehlgeschlagen.", type: "error" });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
